import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  feedApiV1MeNotificationsFeedGet,
  listThreadsApiV1CasesCaseIdThreadsGet,
  type NotificationFeedOut,
  type NotificationOut,
  readAllApiV1MeNotificationsReadAllPost,
  readOneApiV1MeNotificationsNotificationIdReadPost,
  routeApiV1InboundInboundIdRoutePost,
  type SearchHit,
  searchApiV1SearchGet,
  snoozeApiV1MeNotificationsNotificationIdSnoozePost,
  type ThreadOut,
  type UnroutedInboundOut,
  unroutedApiV1InboundUnroutedGet,
} from '../../lib/api/generated'

export type NotificationCategory = NotificationOut['category']

export const notificationKeys = {
  all: ['notifications'] as const,
  inbound: ['notifications', 'inbound'] as const,
  feed: (category?: NotificationCategory, includeSnoozed = false, limit = 50) =>
    ['notifications', 'feed', category ?? 'all', includeSnoozed, limit] as const,
}

export const unroutedInboundQuery = () =>
  queryOptions({
    queryKey: notificationKeys.inbound,
    queryFn: () => unwrap(unroutedApiV1InboundUnroutedGet({ query: { limit: 200 } })),
    staleTime: 15_000,
    retry: 1,
  })

export const inboundCaseSearchQuery = (q: string) =>
  queryOptions({
    queryKey: ['notifications', 'inbound-case-search', q] as const,
    queryFn: async (): Promise<SearchHit[]> => {
      const result = await searchApiV1SearchGet({ query: { q } })
      if (result.error === undefined && Array.isArray(result.data)) return result.data.filter((hit) => hit.kind === 'case')
      const body = (result.error ?? {}) as { local_results?: SearchHit[] }
      if (Array.isArray(body.local_results)) return body.local_results.filter((hit) => hit.kind === 'case')
      throw result.error
    },
    enabled: q.trim().length > 0,
  })

export const inboundThreadsQuery = (caseId: string | null) =>
  queryOptions({
    queryKey: ['notifications', 'inbound-threads', caseId] as const,
    queryFn: (): Promise<ThreadOut[]> => unwrap(listThreadsApiV1CasesCaseIdThreadsGet({ path: { case_id: caseId as string } })),
    enabled: Boolean(caseId),
  })

export function useRouteInbound() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ inboundId, caseId, threadId }: { inboundId: string; caseId: string; threadId: string }) =>
      unwrap(
        routeApiV1InboundInboundIdRoutePost({
          path: { inbound_id: inboundId },
          body: { case_id: caseId, thread_id: threadId },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export type { UnroutedInboundOut }

export const notificationsFeedQuery = (options: { category?: NotificationCategory; includeSnoozed?: boolean; limit?: number } = {}) => {
  const limit = options.limit ?? 50
  const includeSnoozed = options.includeSnoozed ?? false
  return queryOptions({
    queryKey: notificationKeys.feed(options.category, includeSnoozed, limit),
    queryFn: async ({ signal }): Promise<NotificationFeedOut> => {
      const out = await unwrap(
        feedApiV1MeNotificationsFeedGet({
          query: {
            limit,
            category: options.category,
            include_snoozed: includeSnoozed,
          },
          signal,
        }),
      )
      if (!out || !Array.isArray(out.items) || typeof out.unread !== 'number') throw new Error('notification inbox answer malformed')
      return out
    },
    staleTime: 30_000,
    retry: 1,
  })
}

function useRefresh() {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
}

export function useReadNotification() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (id: string) => unwrap(readOneApiV1MeNotificationsNotificationIdReadPost({ path: { notification_id: id } })),
    onSettled: refresh,
  })
}

export function useReadAllNotifications() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (category?: NotificationCategory) =>
      unwrap(readAllApiV1MeNotificationsReadAllPost({ body: { category: category ?? null } })),
    onSettled: refresh,
  })
}

export function useSnoozeNotification() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: ({ id, until }: { id: string; until: string | null }) =>
      unwrap(snoozeApiV1MeNotificationsNotificationIdSnoozePost({ path: { notification_id: id }, body: { until } })),
    onSettled: refresh,
  })
}
